import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Plus, Upload, MapPin } from 'lucide-react';
import { hasFullAccess, hasNoScope } from '../lib/permissions';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ConstituenciesPage() {
  const { user } = useAuth();
  const [constituencies, setConstituencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newConstituency, setNewConstituency] = useState({ name: '' });

  useEffect(() => {
    loadConstituencies();
  }, []);

  const loadConstituencies = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/constituencies`, { withCredentials: true });
      setConstituencies(response.data);
    } catch (error) {
      toast.error('Failed to load constituencies');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!newConstituency.name) {
      toast.error('Please enter constituency name');
      return;
    }

    try {
      await axios.post(`${API}/constituencies`, newConstituency, { withCredentials: true });
      toast.success('Constituency created');
      setShowAddDialog(false);
      setNewConstituency({ name: '' });
      loadConstituencies();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create');
    }
  };

  return (
    <Layout>
      <div className="p-4 md:p-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
          <div>
            <h1 className="text-4xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Constituencies
            </h1>
            <p className="text-muted-foreground">Manage your constituencies and campaigns</p>
          </div>
          {(hasFullAccess(user) || user?.effective_permissions?.includes('constituencies.create')) && (
            <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
              <DialogTrigger asChild>
                <Button className="mt-4 md:mt-0">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Constituency
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add Constituency</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label>Constituency Name</Label>
                    <Input
                      value={newConstituency.name}
                      onChange={(e) => setNewConstituency({ name: e.target.value })}
                      placeholder="e.g., Mahchangolhi Medhu"
                    />
                  </div>
                  <Button onClick={handleCreate} className="w-full">Create</Button>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        ) : constituencies.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <MapPin className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              {hasNoScope(user) ? (
                <>
                  <h3 className="text-lg font-semibold mb-2">No constituencies assigned</h3>
                  <p className="text-muted-foreground mb-4">
                    Your account has not been given access to any constituency yet.
                    Ask an administrator to assign one.
                  </p>
                </>
              ) : (
                <>
                  <h3 className="text-lg font-semibold mb-2">No constituencies yet</h3>
                  <p className="text-muted-foreground mb-4">Add your first constituency to get started</p>
                </>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {constituencies.map(constituency => (
              <Card key={constituency.constituency_id} className="cursor-pointer hover:shadow-lg transition-shadow"
                onClick={() => window.location.href = `/campaigns?constituency_id=${constituency.constituency_id}`}>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MapPin className="h-5 w-5 text-accent" />
                    {constituency.name}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-sm text-muted-foreground">
                    Click to view campaigns
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}